import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { smmOrdersApi } from '../lib/api';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { TableScroll, StatTile } from '../components/ResponsiveTable';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ShoppingCart, RefreshCw, ExternalLink, AlertCircle, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * SMM Orders - the Aurora replacement for the legacy Thymeleaf /smmOrders/ page.
 *
 * Two things drive the design here.
 *
 * 1. `partial` is not a synonym for `completed`. A partial order is one the panel refunded the
 *    undelivered remainder of, so it gets its own warning-toned badge and its own stat tile.
 *    Collapsing it into "completed" would hide real refunds from whoever is reading this page.
 *
 * 2. A tenant can hold ~10,000 orders. The API caps what it returns, so the header states
 *    "showing N of M" from the summary rather than implying the table is everything.
 */

const STATUS_TONE = {
  completed: 'border-success/40 text-success',
  partial: 'border-warning/40 text-warning',
  canceled: 'border-danger/40 text-danger',
  cancelled: 'border-danger/40 text-danger',
  inProgress: 'border-info/40 text-info',
  justReceived: 'border-info/40 text-info',
};

function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant="outline" className={cn('whitespace-nowrap', STATUS_TONE[status] || 'border-border text-muted-foreground')}>
      {status}
    </Badge>
  );
}

/* "3 of 13" plus a thin bar - the delivery position at a glance, without a chart. */
function Delivered({ quantity, remaining }) {
  const q = Number(quantity) || 0;
  const r = Number(remaining) || 0;
  const done = Math.max(0, q - r);
  const pct = q > 0 ? Math.min(100, Math.round((done / q) * 100)) : 0;
  return (
    <div className="min-w-[92px]">
      <span className="tnum text-sm text-foreground">{done}<span className="text-muted-foreground"> / {q}</span></span>
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full', pct >= 100 ? 'bg-success' : 'bg-primary')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function shortUrl(url) {
  if (!url) return '—';
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
}

/*
 * Not every target is a URL. A urlFollow order's target is a bare username ("therock"), and
 * rendering that as <a href="therock"> produced a RELATIVE link - clicking it navigated out of
 * the SPA to /therock. Only linkify a real absolute http(s) target.
 */
function isExternalUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

/*
 * 126 comma-separated slot names is not a readable tooltip - and orders that size are normal here.
 * Collapse consecutive slots into ranges, the same shorthand the settings grid uses, so
 * "slot6, slot7, ... slot131" reads as "6-131" and a scattered set reads as "6-11, 21-30, 43".
 * Anything not shaped like slotN is left exactly as it came.
 */
function compactSlots(csv) {
  if (!csv) return null;
  const nums = csv
    .split(',')
    .map(x => parseInt(String(x).trim().replace(/^slot/i, ''), 10))
    .filter(n => Number.isFinite(n))
    .sort((a, b) => a - b);
  if (!nums.length) return csv;

  const parts = [];
  let start = nums[0];
  let prev = nums[0];
  for (let i = 1; i <= nums.length; i++) {
    const n = nums[i];
    if (n === prev) continue;               // duplicates would otherwise split a run
    if (n === prev + 1) { prev = n; continue; }
    parts.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = n;
    prev = n;
  }
  return parts.join(', ');
}

/*
 * A slot number with its slot list on hover. The counts are what fit in a column; the CSV can run
 * to a hundred-plus entries, so it lives in the tooltip rather than the cell.
 */
function SlotCount({ n, csv, label, className }) {
  const count = Number(n) || 0;
  const list = compactSlots(csv);
  const tip = count === 0 ? `no ${label} slots` : `${label} (${count}): ${list || 'not recorded'}`;
  return (
    <span
      title={tip}
      aria-label={tip}
      className={cn('tnum', count > 0 && list && 'cursor-help border-b border-dotted border-current', className)}
    >
      {count}
    </span>
  );
}

function when(value) {
  if (!value) return '—';
  return String(value).replace('T', ' ').slice(0, 16);
}

/*
 * Service column. Both values come from one line of the SMM Services Settings Grid, e.g.
 *   14 | 1-8, 10, 11, ... | viewViaUrlWithLikeWithSaveWithComment | fast | 72 | ...
 *      ^ service id                ^ target action id
 * The id is what an operator matches against that grid, so it is what we show; the action is
 * long and only needed occasionally, so it rides along in the tooltip.
 */
function ServiceCell({ serviceId, action, className }) {
  const id = (serviceId === null || serviceId === undefined || serviceId === '') ? '—' : String(serviceId);
  const tip = action ? `service ${id} · ${action}` : `service ${id}`;
  return (
    <span
      title={tip}
      aria-label={tip}
      className={cn(
        'tnum font-medium text-foreground',
        action && 'cursor-help border-b border-dotted border-muted-foreground/50',
        className
      )}
      data-testid="smm-service-cell"
    >
      {id}
    </span>
  );
}

/*
 * Delete is destructive and irreversible, so it asks first and says plainly what it does and does
 * NOT do: the local record goes, the order on the panel is untouched. Same operation the legacy
 * page's "Delete Order" link performs, which also clears the order out of every slot it was
 * assigned to.
 */
function DeleteOrderButton({ orderId, pending, onDelete }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={pending}
          title={`Delete order #${orderId}`}
          aria-label={`Delete order #${orderId}`}
          className="text-muted-foreground hover:text-danger hover:bg-danger/10"
          data-testid="smm-order-delete"
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete order #{orderId}?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the local record and clears the order from every slot it was assigned to.
            It cannot be undone. The order on your SMM panel is not changed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onDelete}
            className="bg-danger text-danger-foreground hover:bg-danger/90"
            data-testid="smm-order-delete-confirm"
          >
            Delete order
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function SmmOrdersPage() {
  usePageTitle('SMM Orders');
  const [status, setStatus] = useState('all');

  const summaryQ = useQuery({
    queryKey: ['smm-orders-summary'],
    queryFn: () => smmOrdersApi.summary().then(res => res.data),
  });

  const ordersQ = useQuery({
    queryKey: ['smm-orders', status],
    queryFn: () => smmOrdersApi.list({ status: status === 'all' ? undefined : status }).then(res => res.data),
  });

  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState(null);

  const removeMut = useMutation({
    mutationFn: (orderId) => smmOrdersApi.remove(orderId),
    onSuccess: (_res, orderId) => {
      toast.success(`Order #${orderId} deleted`);
      // refetch both: the list AND the totals, or the header would keep claiming the old count
      queryClient.invalidateQueries({ queryKey: ['smm-orders'] });
      queryClient.invalidateQueries({ queryKey: ['smm-orders-summary'] });
    },
    onError: (err, orderId) => {
      const detail = err?.response?.data?.error || err?.message || 'unknown error';
      toast.error(`Could not delete order #${orderId}: ${detail}`);
    },
    onSettled: () => setPendingId(null),
  });

  const summary = summaryQ.data;
  const orders = Array.isArray(ordersQ.data) ? ordersQ.data : [];
  const byStatus = (summary && summary.by_status) || {};

  const refreshing = ordersQ.isFetching || summaryQ.isFetching;
  const refresh = () => { ordersQ.refetch(); summaryQ.refetch(); };

  const mobileList = orders.map(o => (
    <div key={o.id ?? o.order_id} className="surface p-3" data-testid="smm-order-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium tnum text-foreground">
            #{o.order_id}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              svc <ServiceCell serviceId={o.service_id} action={o.target_action_id} />
            </span>
          </p>
          <p className="truncate text-xs text-muted-foreground">{shortUrl(o.target_url)}</p>
        </div>
        <StatusBadge status={o.status} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <Delivered quantity={o.quantity} remaining={o.remaining} />
        <span className="text-xs text-muted-foreground">{when(o.ordered_at)}</span>
      </div>
    </div>
  ));

  return (
    <div data-testid="smm-orders-page">
      <PageHeader
        icon={ShoppingCart}
        title="SMM Orders"
        description={
          summary
            ? `${orders.length} shown of ${summary.total} orders · ${summary.open} still open`
            : 'Orders pulled from your SMM panel'
        }
        actions={
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[150px]" data-testid="smm-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open only</SelectItem>
                {Object.keys(byStatus).sort().map(s => (
                  <SelectItem key={s} value={s}>{s} ({byStatus[s]})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={refresh} disabled={refreshing} title="Refresh">
              <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </Button>
          </div>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Total" value={summary ? summary.total : '—'} icon={ShoppingCart} tone="primary" testId="stat-total" />
        <StatTile label="Open" value={summary ? summary.open : '—'} tone="info" sub="still being delivered" testId="stat-open" />
        {/* Once the summary has loaded, a missing status means zero of them - showing "—" there
            would read as "unknown" when the truthful answer is "none". */}
        <StatTile
          label="Completed"
          value={summary ? (byStatus.completed || 0) : '—'}
          tone="success"
          testId="stat-completed"
        />
        <StatTile
          label="Partial"
          value={summary ? (byStatus.partial || 0) : '—'}
          tone="warning"
          sub="remainder refunded"
          testId="stat-partial"
        />
      </div>

      {ordersQ.isError && (
        <div className="surface flex items-center gap-2 p-4 text-danger" data-testid="smm-orders-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-sm">Could not load orders. {String(ordersQ.error?.message || '')}</span>
        </div>
      )}

      {ordersQ.isLoading && (
        <div className="space-y-2" data-testid="smm-orders-loading">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-secondary/60" />
          ))}
        </div>
      )}

      {!ordersQ.isLoading && !ordersQ.isError && orders.length === 0 && (
        <div className="surface p-8 text-center" data-testid="smm-orders-empty">
          <ShoppingCart className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            {status === 'all'
              ? 'No SMM orders yet. Orders appear here once your panel sends them.'
              : `No orders with status "${status}".`}
          </p>
        </div>
      )}

      {!ordersQ.isLoading && !ordersQ.isError && orders.length > 0 && (
        <TableScroll mobile={mobileList} minWidth="940px">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Order</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead className="text-right">Slots</TableHead>
                <TableHead>Placed</TableHead>
                <TableHead>Closed</TableHead>
                <TableHead className="w-10 text-right"><span className="sr-only">Delete</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map(o => (
                <TableRow key={o.id ?? o.order_id} className="border-border" data-testid="smm-order-row">
                  <TableCell className="tnum font-medium">#{o.order_id}</TableCell>
                  <TableCell>
                    <ServiceCell serviceId={o.service_id} action={o.target_action_id} />
                  </TableCell>
                  <TableCell className="max-w-[240px]">
                    {isExternalUrl(o.target_url) ? (
                      <a
                        href={o.target_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 truncate text-primary hover:underline"
                        title={o.target_url}
                      >
                        <span className="truncate">{shortUrl(o.target_url)}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="block truncate text-foreground" title={o.target_url || ''}>
                        {o.target_url || '—'}
                      </span>
                    )}
                  </TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell><Delivered quantity={o.quantity} remaining={o.remaining} /></TableCell>
                  <TableCell className="text-right text-sm">
                    <SlotCount n={o.assigned_slots_count} csv={o.assigned_slots_csv} label="assigned"
                      className="text-foreground" />
                    {' / '}
                    <SlotCount n={o.success_slots} csv={o.success_slots_csv} label="delivered"
                      className="text-success" />
                    {' / '}
                    <SlotCount n={o.failed_slots} csv={o.failed_slots_csv} label="failed"
                      className={cn(o.failed_slots > 0 ? 'text-danger' : 'text-muted-foreground')} />
                    {o.invalid_slots > 0 && (
                      <>
                        {' / '}
                        <SlotCount n={o.invalid_slots} csv={o.invalid_slots_csv} label="invalid target"
                          className="text-warning" />
                      </>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      assigned / ok / failed{o.invalid_slots > 0 ? ' / invalid' : ''} · hover for slots
                    </p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{when(o.ordered_at)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{when(o.completed_at)}</TableCell>
                  <TableCell className="text-right">
                    <DeleteOrderButton
                      orderId={o.order_id}
                      pending={removeMut.isPending && pendingId === o.order_id}
                      onDelete={() => { setPendingId(o.order_id); removeMut.mutate(o.order_id); }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      )}

      {summary && orders.length > 0 && orders.length < summary.total && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="smm-orders-truncated">
          Showing {orders.length} of {summary.total}. Use the status filter to narrow the list.
        </p>
      )}
    </div>
  );
}
